import React from 'react';
import { View, Text, Pressable } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';

const KIND_LABELS = {
    board: 'Boards & Albums',
    calendar: 'Calendars',
    folder: 'Library Folders',
};
const KIND_ORDER = ['board', 'calendar', 'folder'];

const boardSubtitle = (board) => {
    const p = board?.rolePerms;
    const perms = [p?.isAdmin && 'Admin', p?.canPost && 'Post', p?.canComment && 'Comment', p?.canReact && 'React'].filter(Boolean);
    return perms.length ? perms.join(' · ') : 'Can view';
};

// Flat list of a role's shared resources in display order. Boards carry the
// role's grant flags (attached to `board.rolePerms` by getRole) for the
// subtitle; calendars/folders are simple links.
export const getRoleResources = (role) => {
    const resources = [];
    for (const b of role?.Boards || []) {
        resources.push({
            kind: 'board',
            id: b.id,
            icon: b.type === 'PHOTO_ALBUM' ? 'images-outline' : 'newspaper-outline',
            name: b.name,
            subtitle: boardSubtitle(b),
        });
    }
    for (const c of role?.Calendars || []) {
        resources.push({ kind: 'calendar', id: c.id, icon: 'calendar-outline', name: c.name, color: c.color });
    }
    for (const f of role?.Folders || []) {
        resources.push({ kind: 'folder', id: f.id, icon: 'folder-outline', name: f.name });
    }
    return resources;
};

export const groupRoleResources = (resources) => {
    const groups = [];
    for (const kind of KIND_ORDER) {
        const items = resources.filter(r => r.kind === kind);
        if (items.length) groups.push({ kind, label: KIND_LABELS[kind], items });
    }
    return groups;
};

const RoleResourcesList = ({ role, onNavigate }) => {
    const router = useRouter();
    const { data: allCalendars } = Query.calendars.all();
    const groups = groupRoleResources(getRoleResources(role));

    if (!groups.length) return null;

    const openResource = async (res) => {
        onNavigate?.();
        switch (res.kind) {
            case 'board':
                router.push({ pathname: '/connect/boards/[boardID]/feed', params: { boardID: res.id } });
                break;
            case 'folder':
                router.push({ pathname: '/library/folders/[folderID]', params: { folderID: res.id } });
                break;
            case 'calendar': {
                // Events has no per-calendar route — isolate the calendar in the
                // shared exclusion filter so it opens showing only its events.
                // Guard: if the list isn't loaded or the calendar is missing,
                // an exclusion of every known id would hide the target too.
                if (allCalendars?.some(c => c.id === res.id)) {
                    const others = allCalendars.map(c => c.id).filter(id => id !== res.id);
                    await AsyncStorage.setItem('@filteredCalendars', JSON.stringify(others));
                }
                router.push('/events');
                break;
            }
        }
    };

    return (
        <View>
            {groups.map(group => (
                <View key={group.kind} style={styles.group}>
                    <Text style={styles.groupLabel}>{group.label}</Text>
                    <View style={styles.groupCard}>
                        {group.items.map((res, i) => (
                            <Pressable
                                key={res.id}
                                testID={`role-resource-${res.kind}-${res.id}`}
                                accessibilityRole="link"
                                accessibilityLabel={`Open ${res.name}`}
                                onPress={() => openResource(res)}
                                style={({ pressed }) => [styles.row, i < group.items.length - 1 && styles.rowDivider, pressed && styles.rowPressed]}
                            >
                                <View style={styles.iconWrap}>
                                    <Unicon name={res.icon} size={17} colorVariant="primary" />
                                </View>
                                <View style={styles.info}>
                                    <Text style={styles.name} numberOfLines={1}>{res.name}</Text>
                                    {!!res.subtitle && <Text style={styles.subtitle} numberOfLines={1}>{res.subtitle}</Text>}
                                </View>
                                <Unicon name="chevron-forward" size={15} colorVariant="textSecondary" />
                            </Pressable>
                        ))}
                    </View>
                </View>
            ))}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, typography, spacing }) => ({
    group: {
        marginBottom: spacing.sm,
    },
    groupLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        marginBottom: 6,
        marginLeft: 2,
    },
    groupCard: {
        backgroundColor: colors.surfaceVariant,
        borderRadius: 10,
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 10,
        paddingHorizontal: 12,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    rowDivider: {
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    rowPressed: {
        backgroundColor: colors.hover,
    },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    info: {
        flex: 1,
    },
    name: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    subtitle: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 1,
    },
}));

export default RoleResourcesList;
